import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Briefcase, CheckCircle, ArrowsClockwise, Users } from "@phosphor-icons/react";

export default function AdminFreelance() {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["platform-settings"], queryFn: async () => (await api.get("/settings/platform")).data });
  const { data: ord } = useQuery({ queryKey: ["fl-admin-orders"], queryFn: async () => (await api.get("/freelance/admin/orders")).data.orders });
  const { data: fl } = useQuery({ queryKey: ["fl-admin-freelancers"], queryFn: async () => (await api.get("/freelance/admin/freelancers")).data.freelancers });
  const s = data?.settings;
  const save = async (patch) => {
    setSaving(true);
    try { await api.put("/settings/platform", patch); toast.success("Saved"); qc.invalidateQueries({ queryKey: ["platform-settings"] }); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  if (!s) return <div className="text-muted-foreground">Loading…</div>;
  const pct = s.freelance_commission_pct ?? 0;
  const orders = ord || [];
  const revenue = orders.filter((o) => o.status === "completed").reduce((a, o) => a + (o.commission_cents || 0), 0);
  const escrow = orders.filter((o) => o.escrowed).reduce((a, o) => a + (o.amount_cents || 0), 0);
  const tone = (st) => st === "completed" ? "bg-emerald-500/15 text-emerald-600" : st === "cancelled" ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600";

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-freelance">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">iYeneh Freelance</h1>
        <p className="mt-1 text-sm text-muted-foreground">Open marketplace for gigs & jobs with wallet escrow. Set your commission and monitor orders.</p>
      </div>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><Briefcase size={20} weight="duotone" /></div>
          <div><h2 className="font-display text-lg font-semibold">Availability & commission</h2><p className="text-sm text-muted-foreground">Turn the marketplace on/off and set your platform commission.</p></div>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="freelance-enabled" checked={!!s.freelance_enabled} onChange={(e) => save({ freelance_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Freelance marketplace enabled</span>
        </label>
        <div className="mt-4 flex items-center gap-2">
          <input data-testid="freelance-commission" type="number" min="0" max="100" step="1" defaultValue={pct.toString()}
            onBlur={(e) => { const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)); if (v !== pct) save({ freelance_commission_pct: v }); }}
            className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <span className="text-sm text-muted-foreground">% commission of each completed order</span>
          <CheckCircle size={14} weight="fill" className="ml-2 text-primary" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Featured job fee $</span>
          <input data-testid="freelance-feature-fee" type="number" min="0" step="1" defaultValue={((s.freelance_feature_fee_cents ?? 500) / 100).toString()}
            onBlur={(e) => { const v = Math.max(0, Math.round((Number(e.target.value) || 0) * 100)); if (v !== (s.freelance_feature_fee_cents ?? 500)) save({ freelance_feature_fee_cents: v }); }}
            className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <span className="text-sm text-muted-foreground">pinned for</span>
          <input data-testid="freelance-feature-days" type="number" min="1" step="1" defaultValue={(s.freelance_feature_days ?? 7).toString()}
            onBlur={(e) => { const v = Math.max(1, Number(e.target.value) || 7); if (v !== (s.freelance_feature_days ?? 7)) save({ freelance_feature_days: v }); }}
            className="w-20 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <span className="text-sm text-muted-foreground">days</span>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-card p-4"><p className="text-xs text-muted-foreground">Commission earned</p><p className="mt-1 font-display text-2xl font-semibold text-emerald-600" data-testid="fl-revenue">{money(revenue)}</p></div>
        <div className="rounded-2xl border border-white/10 bg-card p-4"><p className="text-xs text-muted-foreground">Held in escrow</p><p className="mt-1 font-display text-2xl font-semibold" data-testid="fl-escrow">{money(escrow)}</p></div>
        <div className="rounded-2xl border border-white/10 bg-card p-4"><p className="text-xs text-muted-foreground">Freelancers</p><p className="mt-1 font-display text-2xl font-semibold" data-testid="fl-count">{(fl || []).length}</p></div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="fl-orders">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Users size={18} weight="duotone" /> Orders</h2>
        <div className="mt-4 space-y-2">
          {orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`fl-order-${o.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-background px-3 py-2 text-sm">
              <span className="min-w-0"><span className="font-semibold">{o.title}</span> · {o.client_name} → {o.freelancer_name}</span>
              <div className="flex items-center gap-2">
                <span>{money(o.amount_cents)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone(o.status)}`}>{o.status}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {saving && <p className="flex items-center gap-2 text-sm text-muted-foreground"><ArrowsClockwise size={14} className="animate-spin" /> Saving…</p>}
    </div>
  );
}
